import type { ChannelsSummary, MeResponse, SoNhapDoiSoat } from '@qlk/shared';
import { useQuery } from '@tanstack/react-query';

import { api, type LoiApi } from '../lib/api.js';
import { coQuyen } from '../lib/auth.js';
import { thangHienTai } from './kyChung.js';

/**
 * Hai con số nhỏ cạnh mục menu: **số kênh** và **số bản nháp đối soát**.
 *
 * Gọi `api` trực tiếp chứ không dùng hook của module `channels` / `team`: khung
 * ứng dụng không nên phụ thuộc vào module nào, nếu không đổi một hook trong
 * module là sidebar hỏng theo.
 */

/** Số kênh — dùng kỳ `28d` vì đó là kỳ mặc định của bảng Quản lý kênh. */
export function useSoKenh(me: MeResponse) {
  const duoc = coQuyen(me.permissions, 'channels', 'view');

  return useQuery<ChannelsSummary, LoiApi>({
    queryKey: ['channels', 'summary', '28d'] as const,
    queryFn: () => api.get<ChannelsSummary>('/api/v1/channels/summary?period=28d'),
    enabled: duoc,
    /* Số kênh gần như không đổi trong một buổi làm việc. */
    staleTime: 5 * 60_000,
  });
}

/**
 * Số bản nháp của **kỳ hiện tại** trong Đối soát nhân sự.
 *
 * Gọi `GET /team/doi-soat/so-nhap` — endpoint **chỉ đếm**, không tính tiền (xem
 * `demBanNhapChuaChot` ở server). Bản trước gọi chính `GET /team/doi-soat` mà
 * trang Đối soát dùng, tức tính lại doanh thu của mọi kênh chỉ để lấy một con
 * số; đổi lại nó hâm sẵn cache cho trang đó, nay không còn — mở trang Đối soát
 * phải chờ lượt gọi thật của nó, và đó là cái giá đúng để thanh bên nhẹ đi.
 *
 * Vẫn chỉ gọi khi người xem có `team:admin` (người khác không mở được trang
 * đó), `staleTime` 5 phút.
 */
export function useSoNhapDoiSoat(me: MeResponse) {
  const duoc = coQuyen(me.permissions, 'team', 'admin');
  const ky = thangHienTai();

  return useQuery<SoNhapDoiSoat, LoiApi>({
    queryKey: ['team', 'doi-soat', 'so-nhap', ky] as const,
    queryFn: () => api.get<SoNhapDoiSoat>(`/api/v1/team/doi-soat/so-nhap?period=${ky}`),
    enabled: duoc,
    staleTime: 5 * 60_000,
  });
}
